/**
 * Promise wrappers around the shared http client for the SaaS billing API.
 *
 * Every rejection is an ``Error`` whose ``detail`` holds the backend's
 * human-readable message (``err.detail``), matching the FastAPI contract.
 * A failure callback is always passed to the client so it never falls back
 * to its own toast — callers decide how to surface errors.
 */
import { data_from_server_delete, data_from_server_get, data_from_server_post } from '@/shared/http/client';

const toError = (detail, fallback) => {
    const message = typeof detail === 'string' && detail.trim() ? detail : fallback;
    const err = new Error(message);
    err.detail = message;
    return err;
};

/**
 * ``data_from_server_get`` / ``data_from_server_delete`` hand the parsed body
 * to the success callback even on non-2xx (e.g. a 409 ``{detail}``), so a
 * response that lacks ``expectedKey`` but carries ``detail`` is an error.
 */
const looksLikeError = (payload, expectedKey) =>
    payload
    && typeof payload === 'object'
    && !(expectedKey in payload)
    && 'detail' in payload;

const getJson = (suffix, expectedKey, fallback) =>
    new Promise((resolve, reject) => {
        data_from_server_get(suffix, (payload) => {
            if (looksLikeError(payload, expectedKey)) {
                reject(toError(
                    typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail),
                    fallback
                ));
                return;
            }
            resolve(payload);
        }).catch((e) => reject(toError(e?.detail || e?.message, fallback)));
    });

const postJson = (suffix, body, fallback, ...clientOptions) =>
    new Promise((resolve, reject) => {
        data_from_server_post(
            suffix,
            body,
            resolve,
            (detail) => reject(toError(detail, fallback)),
            ...clientOptions
        ).catch((e) => reject(toError(e?.detail || e?.message, fallback)));
    });

const deleteJson = (suffix, body, expectedKey, fallback) =>
    new Promise((resolve, reject) => {
        data_from_server_delete(
            suffix,
            body,
            (payload) => {
                if (looksLikeError(payload, expectedKey)) {
                    reject(toError(
                        typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail),
                        fallback
                    ));
                    return;
                }
                resolve(payload);
            },
            (detail) => reject(toError(detail, fallback))
        ).catch((e) => reject(toError(e?.detail || e?.message, fallback)));
    });

const PROFILE_KEY = 'billing_enabled';

/** GET billing/profile → billing profile. */
export function fetchBillingProfile() {
    return getJson('billing/profile', PROFILE_KEY, 'Could not load billing details.');
}

/**
 * GET billing/usage → ``{months: [{month: 'YYYY-MM', spent_usd, operations: [{operation_type, spent_usd}]}]}``,
 * the last 12 months, oldest first.
 */
export function fetchCreditUsage() {
    return getJson('billing/usage', 'months', 'Could not load credit usage.');
}

/** GET billing/usage/since-last-invoice → ``{since: ISO date or null before the first invoice, spent_usd}``. */
export function fetchSpendSinceLastInvoice() {
    return getJson('billing/usage/since-last-invoice', 'spent_usd', 'Could not load credit usage.');
}

/** GET billing/usage/daily?days=N → ``{days: [{day: 'YYYY-MM-DD', spent_usd, operations}]}``, the last *days* days, oldest first. */
export function fetchDailyCreditUsage(days) {
    return getJson(`billing/usage/daily?days=${days}`, 'days', 'Could not load credit usage.');
}

/**
 * GET billing/usage/ledger?limit=N → ``{breakdown, entries}``: why the balance is what it is
 * (``credited_usd - spent_usd - held_usd + adjustment_usd = balance_usd``) plus the newest
 * ``N`` movements, open holds and never-applied rows included.
 */
export function fetchCreditLedger(limit = 50) {
    return getJson(`billing/usage/ledger?limit=${limit}`, 'entries', 'Could not load credit usage.');
}

/** GET billing/usage/downloads → ``{downloads: [{llm_usecase_id, model_name, at, pricing, count}]}``, newest first. */
export function fetchModelDownloads() {
    return getJson('billing/usage/downloads', 'downloads', 'Could not load model downloads.');
}

/** POST billing/setup-intent → ``{client_secret, setup_intent_id}``. */
export function createSetupIntent(purpose) {
    return postJson('billing/setup-intent', { purpose }, 'Could not start the payment method setup.');
}

/** POST billing/payment-methods → profile (attaches a confirmed PaymentMethod). */
export function attachPaymentMethod({ payment_method_id, purpose, replace_payment_method_id }) {
    const body = { payment_method_id, purpose };
    if (replace_payment_method_id) {
        body.replace_payment_method_id = replace_payment_method_id;
    }
    return postJson('billing/payment-methods', body, 'Could not save the payment method.');
}

/** POST billing/payment-methods/{id}/primary → profile. */
export function setPrimaryPaymentMethod(paymentMethodId) {
    return postJson(
        `billing/payment-methods/${encodeURIComponent(paymentMethodId)}/primary`,
        {},
        'Could not update the primary card.'
    );
}

/** DELETE billing/payment-methods/{id} → profile (409 when primary). */
export function deletePaymentMethod(paymentMethodId) {
    return deleteJson(
        `billing/payment-methods/${encodeURIComponent(paymentMethodId)}`,
        {},
        PROFILE_KEY,
        'Could not delete the card.'
    );
}

/**
 * Stripe Customer Portal flows accepted by the backend. Pro is usage-based with a single
 * price, so there is no plan-change ("upgrade") flow; ``'manage'`` opens cards + invoices.
 */
export const PORTAL_FLOWS = Object.freeze({ manage: 'manage' });

/**
 * POST billing/portal-session ``{flow: 'manage'}`` → ``{url}``, or
 * ``{url: null, reason}`` (still a 2xx) when there is nothing to open — see PORTAL_NO_INVOICES_REASON.
 */
export function createPortalSession(flow = PORTAL_FLOWS.manage) {
    return postJson('billing/portal-session', { flow }, 'Could not open the billing portal.');
}

/**
 * `reason` returned by POST billing/portal-session (with `url: null`, HTTP 200) when the
 * `manage` flow has nothing to show because the customer was never invoiced.
 */
export const PORTAL_NO_INVOICES_REASON = 'no_invoices';

/**
 * POST billing/token-top-up ``{amount_usd}`` → ``{url, session_id}``: a Stripe Checkout
 * session for a one-off credit purchase. ``amount_usd`` is a whole-dollar amount between
 * ``profile.tokens.top_up_min_usd`` and ``top_up_max_usd`` (422 otherwise); the credits
 * are added by the Checkout webhook, not by this call.
 */
export function createTokenTopUpSession(amountUsd) {
    return postJson('billing/token-top-up', { amount_usd: amountUsd }, 'Could not start the credit purchase.');
}

/**
 * POST billing/auto-charge ``{enabled, amount_usd, threshold_usd, monthly_limit_usd}`` → profile.
 * ``amount_usd`` is added whenever the balance drops to ``threshold_usd`` (0 = when the credits
 * run out); ``monthly_limit_usd`` caps a calendar month, ``null`` for no cap (422 when below the
 * amount). Turning it on needs a valid primary card (409).
 */
export function updateAutoCharge({ enabled, amount_usd, threshold_usd = 0, monthly_limit_usd = null }) {
    return postJson(
        'billing/auto-charge',
        { enabled, amount_usd, threshold_usd, monthly_limit_usd },
        'Could not save the auto-recharge settings.'
    );
}

/**
 * POST billing/low-balance-alert ``{enabled, threshold_usd, recipient_user_ids}`` → profile.
 * Workspace Admins are always emailed; ``recipient_user_ids`` are the tagged teammates who
 * are emailed as well (422 when an id is not a member of the workspace). ``extra_emails``
 * is still accepted for callers that have not moved to user ids.
 */
export function updateLowBalanceAlert({ enabled, threshold_usd, recipient_user_ids, extra_emails }) {
    const body = { enabled, threshold_usd };
    if (recipient_user_ids !== undefined) body.recipient_user_ids = recipient_user_ids;
    if (extra_emails !== undefined) body.extra_emails = extra_emails;
    return postJson(
        'billing/low-balance-alert',
        body,
        'Could not save the low balance alert.'
    );
}

/**
 * POST billing/coupon ``{code}`` → profile. Adds the USD value of a platform coupon (created by a
 * god in the Admin Console) to the prepaid credits: 404 for an unknown, expired, revoked or
 * other-workspace code, 409 when this workspace already used it or its redemption cap is reached.
 */
export function redeemCoupon(code) {
    return postJson('billing/coupon', { code }, 'Could not redeem this coupon.');
}

export const DOWNLOAD_QUOTE_ERROR = 'Could not check download cost. Try again.';

/**
 * POST download/quote ``[{llm_usecase_id, version}]`` → what requesting those model
 * downloads would cost: ``{items: [{llm_usecase_id, version, reference_id, cost_cents,
 * pricing: 'already_recorded'|'free'|'paid'}], total_cents, balance_cents,
 * free_downloads_remaining, blocked_reason: 'free_usage_exhausted'|'insufficient_credits'|null,
 * enforcement_exempt}``. The quote never 402s, and the gate it feeds owns the
 * billing modals, so the client's free-usage announcement is turned off here.
 */
export function fetchDownloadQuote(items) {
    return postJson('download/quote', items, DOWNLOAD_QUOTE_ERROR, { announceFreeUsageExhausted: false });
}

export const COST_ESTIMATE_ERROR = 'Could not estimate the cost.';

/**
 * POST billing/estimate ``{operation, params}`` → what running that operation would cost before
 * it starts: ``{operation, estimated_tokens, estimated_usd, max_usd, breakdown: [{operation_type,
 * tokens, usd}], records, note, unavailable_reason}``. ``operation`` is one of ``train``,
 * ``revaluate``, ``data_generation``, ``label_generation``, ``data_retrieval``, ``model_download``,
 * ``model_deployment``. 404 off the SaaS platform; a quote never 402s, so the client's free-usage
 * announcement is turned off here.
 */
export function fetchCostEstimate(operation, params) {
    return postJson(
        'billing/estimate',
        { operation, params },
        COST_ESTIMATE_ERROR,
        { announceFreeUsageExhausted: false }
    );
}

/** GET billing/cancellation-reasons → ``{reasons: [{id, label}]}``. */
export function fetchCancellationReasons() {
    return getJson('billing/cancellation-reasons', 'reasons', 'Could not load cancellation reasons.').then(
        (payload) => (Array.isArray(payload?.reasons) ? payload.reasons : [])
    );
}

/**
 * POST billing/cancel ``{reason_ids?, comment?}`` → profile. Removes every saved card and turns
 * auto-recharge off; the remaining credits stay usable (409 while a recharge is in flight).
 * ``reason_ids`` is the multi-select from the Figma chips; a legacy single ``reason_id`` is
 * folded into it.
 */
export function cancelBillingAccount({ reason_ids, reason_id, comment } = {}) {
    const ids = [...(Array.isArray(reason_ids) ? reason_ids : []), ...(reason_id ? [reason_id] : [])].filter(
        (id, index, list) => Boolean(id) && list.indexOf(id) === index
    );
    const body = {};
    if (ids.length > 0) body.reason_ids = ids;
    if (comment) body.comment = comment;
    return postJson('billing/cancel', body, 'Could not cancel the billing account.');
}
